import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaPalette, FaMobileAlt, FaSearch, FaStore } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import { theme } from '../../styles/theme';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.primary};
  padding-top: 80px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  color: ${theme.colors.secondary};
  font-size: 2.5rem;
  margin-bottom: 20px;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.primary};
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const LocationHighlight = styled.span`
  color: ${theme.colors.secondary};
  font-weight: 600;
`;

const Section = styled.div`
  margin-bottom: 80px;
`;

const SectionTitle = styled.h2`
  color: ${theme.colors.secondary};
  font-size: 2rem;
  margin-bottom: 30px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 30px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(1, 191, 113, 0.1);
  }
`;

const CardIcon = styled.div`
  color: #01bf71;
  font-size: 2.5rem;
  margin-bottom: 20px;
`;

const CardTitle = styled.h3`
  color: #fff;
  font-size: 1.5rem;
  margin-bottom: 15px;
`;

const CardText = styled.p`
  color: #fff;
  font-size: 1rem;
  line-height: 1.6;
`;

const FeaturesList = styled.div`
  margin-top: 20px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const FeatureIcon = styled.div`
  color: #01bf71;
  font-size: 1.2rem;
  margin-right: 10px;
  margin-top: 3px;
`;

const FeatureText = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: #fff;
`;

const Testimonials = styled.div`
  margin-bottom: 80px;
`;

const TestimonialCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 30px;
  margin-bottom: 30px;
`;

const TestimonialText = styled.p`
  color: #fff;
  font-size: 1.1rem;
  line-height: 1.6;
  font-style: italic;
  margin-bottom: 20px;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorName = styled.h4`
  color: #fff;
  font-size: 1.1rem;
  margin: 0;
`;

const AuthorCompany = styled.p`
  color: #01bf71;
  font-size: 0.9rem;
  margin: 5px 0 0;
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #01bf71, #018f55);
  border-radius: 10px;
  padding: 60px;
  text-align: center;
  
  @media screen and (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const CTATitle = styled.h2`
  color: #fff;
  font-size: 2.5rem;
  margin-bottom: 20px;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTAText = styled.p`
  color: #fff;
  font-size: 1.2rem;
  margin-bottom: 30px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: transparent;
  border: 2px solid ${theme.colors.secondary};
  color: ${theme.colors.secondary};
  font-size: 1.2rem;
  font-weight: 600;
  padding: 15px 30px;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.primary};
    color: ${theme.colors.text.primary};
    transform: translateY(-3px);
  }
`;

const PretoriaWebDesign = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>Web Design in Pretoria | Professional Website Design Services | Afrotech Group</title>
        <meta name="description" content="Professional web design services in Pretoria. Beautiful, responsive websites for local businesses with SEO optimization and mobile-friendly designs. Contact Afrotech Group today!" />
        <meta name="keywords" content="web design Pretoria, website design Pretoria, professional web design, responsive websites, Pretoria web designer" />
      </Helmet>
      
      <ContentWrapper>
        <PageHeader>
          <Title>Professional Web Design in <LocationHighlight>Pretoria</LocationHighlight></Title>
          <Subtitle>
            Creating beautiful, functional, and conversion-focused websites for Pretoria businesses
          </Subtitle>
        </PageHeader>
        
        <Section>
          <SectionTitle>Our Web Design Services in Pretoria</SectionTitle>
          <Grid>
            <Card>
              <CardIcon><FaPalette /></CardIcon>
              <CardTitle>Custom Website Design</CardTitle>
              <CardText>
                Unique, branded website designs that reflect your Pretoria business identity and connect with your local audience.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Brand-aligned visual identity</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>User-friendly navigation</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Conversion-optimized layouts</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaMobileAlt /></CardIcon>
              <CardTitle>Responsive Web Design</CardTitle>
              <CardText>
                Websites that look and function perfectly on all devices, ensuring your Pretoria customers have a seamless experience.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Mobile-first approach</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Cross-browser compatibility</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Fast loading on all devices</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaStore /></CardIcon>
              <CardTitle>E-commerce Website Design</CardTitle>
              <CardText>
                Online stores that help Pretoria businesses sell products and services with secure, user-friendly shopping experiences.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>South African payment integration</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Product catalog design</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Secure checkout process</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaSearch /></CardIcon>
              <CardTitle>SEO-Friendly Design</CardTitle>
              <CardText>
                Websites built with search engine optimization in mind, helping your Pretoria business rank higher in local searches.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>SEO-optimized structure</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Fast loading speed</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Local SEO implementation</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
          </Grid>
        </Section>
        
        <Section>
          <SectionTitle>Why Choose Afrotech for Web Design in Pretoria</SectionTitle>
          <Grid>
            <Card>
              <CardTitle>Local Expertise</CardTitle>
              <CardText>
                We understand the Pretoria market and create websites that resonate with local customers and businesses.
              </CardText>
            </Card>
            
            <Card>
              <CardTitle>Results-Driven Approach</CardTitle>
              <CardText>
                Our web designs focus on achieving your business goals, whether that's generating leads, increasing sales, or building brand awareness.
              </CardText>
            </Card>
            
            <Card>
              <CardTitle>Technical Excellence</CardTitle>
              <CardText>
                We combine beautiful design with solid technical implementation for websites that not only look great but perform exceptionally.
              </CardText>
            </Card>
            
            <Card>
              <CardTitle>Ongoing Support</CardTitle>
              <CardText>
                Our Pretoria-based team provides continuous support and maintenance to keep your website secure and up-to-date.
              </CardText>
            </Card>
          </Grid>
        </Section>
        
        <Testimonials>
          <SectionTitle>What Our Pretoria Clients Say</SectionTitle>
          
          <TestimonialCard>
            <TestimonialText>
              "Afrotech Group transformed our outdated website into a modern, responsive platform that perfectly represents our brand. Since the redesign, we've seen a significant increase in inquiries from Pretoria customers."
            </TestimonialText>
            <TestimonialAuthor>
              <div>
                <AuthorName>Thabo Molefe</AuthorName>
                <AuthorCompany>Pretoria Business Solutions</AuthorCompany>
              </div>
            </TestimonialAuthor>
          </TestimonialCard>
          
          <TestimonialCard>
            <TestimonialText>
              "The team at Afrotech understood exactly what we needed for our local Pretoria business. They created a beautiful e-commerce website that's easy to manage and has significantly boosted our online sales."
            </TestimonialText>
            <TestimonialAuthor>
              <div>
                <AuthorName>Sarah Johnson</AuthorName>
                <AuthorCompany>Hatfield Boutique</AuthorCompany>
              </div>
            </TestimonialAuthor>
          </TestimonialCard>
        </Testimonials>
        
        <CTASection>
          <CTATitle>Ready for a Website That Stands Out in Pretoria?</CTATitle>
          <CTAText>
            Contact our Pretoria web design team today for a free consultation and quote. Let's create a website that helps your business grow.
          </CTAText>
          <CTAButton to="/contact">Get a Free Quote</CTAButton>
        </CTASection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default PretoriaWebDesign;
